/**
 * useInactivityLogout — máquina de estados de logout por inatividade.
 *
 * Motivação: a sessão do ConsoleAI tem idle-timeout deslizante de 4h, mas
 * qualquer requisição autenticada a renova — e uma aba aberta gera requisições
 * de background sozinha (SSE de notificações, poll do sino, refetch-on-focus).
 * Resultado: a sessão nunca atinge inatividade REAL enquanto a aba está aberta.
 * Este hook fecha a lacuna no cliente medindo ausência de input do usuário.
 *
 * Fluxo: após INACTIVITY_LIMIT_MS sem input real, abre um AVISO com contagem
 * regressiva de WARNING_SECONDS. Durante o aviso, só o cancelamento explícito
 * (`staySignedIn`, via botão) reverte localmente — atividade local NÃO cancela.
 * Esgotada a contagem → handleLogout().
 *
 * Robusto a abas em segundo plano: a decisão é baseada em TIMESTAMP da última
 * atividade (não na precisão do timer). Um tick periódico reavalia, e o
 * `visibilitychange` reavalia ao voltar o foco — mesmo que o browser congele os
 * timers da aba em background, ao retornar recalcula o tempo decorrido e dispara
 * aviso/logout na hora.
 *
 * Sincronização cross-tab (BroadcastChannel): atividade real em QUALQUER aba
 * difunde "activity" e reseta as demais (cancela avisos); o logout difunde
 * "logout" e desloga todas juntas. Degrada p/ por-aba sem BroadcastChannel.
 *
 * Gateado por feature flag `inactivity_logout_enabled` (default OFF).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/store/auth'
import { useFeatureFlag } from '@/hooks/useFeatureFlags'
import { handleLogout } from '@/auth/handleLogout'
import { isStandalonePWA } from '@/utils/pwa'

// 30 min sem input real → abre o aviso (console com dados sensíveis).
export const INACTIVITY_LIMIT_MS = 30 * 60 * 1000
// Janela de cancelamento do aviso antes do logout efetivo.
export const WARNING_SECONDS = 30
const WARNING_MS = WARNING_SECONDS * 1000
// Cadência de reavaliação (também atualiza a contagem do aviso em foreground).
const TICK_MS = 1000
const CHANNEL_NAME = 'consoleai-inactivity'
const BROADCAST_THROTTLE_MS = 2000

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const

export interface InactivityState {
  warningActive: boolean
  secondsLeft: number
  staySignedIn: () => void
}

export function useInactivityLogout(): InactivityState {
  const enabled = useFeatureFlag('inactivity_logout_enabled')
  const pwaPersistent = useFeatureFlag('pwa_persistent_session')
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const [warningActive, setWarningActive] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS)

  const stayRef = useRef<() => void>(() => {})
  const staySignedIn = useCallback(() => stayRef.current(), [])

  useEffect(() => {
    if (!enabled || !isAuthenticated || (pwaPersistent && isStandalonePWA())) {
      setWarningActive(false)
      return
    }

    let tick: ReturnType<typeof setInterval> | undefined
    let stopped = false
    let warning = false // espelho local (evita stale-closure da state)
    let lastActivity = Date.now()
    let warningStart = 0
    let lastBroadcast = -Infinity

    const bc: BroadcastChannel | null =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null

    const setWarn = (v: boolean) => {
      warning = v
      setWarningActive(v)
    }

    const doLogout = (broadcast: boolean) => {
      if (stopped) return
      stopped = true
      if (tick) clearInterval(tick)
      if (broadcast) bc?.postMessage({ type: 'logout' })
      // Logout involuntário (inatividade): guarda a página atual p/ retorno após o
      // re-login e manda p/ a tela estática /session-expired (sem TTL), em vez do
      // /login que auto-dispara o OIDC. Não guardar as próprias telas de auth.
      try {
        const here = window.location.pathname + window.location.search
        const ret =
          here.startsWith('/login') || here.startsWith('/session-expired')
            ? '/'
            : here
        sessionStorage.setItem('post_login_return_to', ret)
      } catch {
        // ignore (sessionStorage indisponível)
      }
      // Disparado por timer (sem user gesture): o popup de logout cross-domain do
      // handleLogout será bloqueado pelo browser e cai no best-effort. O essencial
      // — encerrar a sessão ConsoleAI + revogar a sessão SSO do broker (end_session,
      // same-site) — funciona; apps *.zt.console caem em ≤15 min (denylist).
      void handleLogout({ redirectTo: '/session-expired', motivo: 'inatividade' })
    }

    const broadcastActivity = () => {
      const now = Date.now()
      if (now - lastBroadcast < BROADCAST_THROTTLE_MS) return
      lastBroadcast = now
      bc?.postMessage({ type: 'activity' })
    }

    // Reavalia o estado com base no tempo decorrido (timestamp-driven).
    const evaluate = () => {
      if (stopped) return
      const now = Date.now()
      if (!warning) {
        if (now - lastActivity >= INACTIVITY_LIMIT_MS) {
          warningStart = now
          setWarn(true)
          setSecondsLeft(WARNING_SECONDS)
        }
      } else {
        const remaining = WARNING_MS - (now - warningStart)
        if (remaining <= 0) {
          setWarn(false)
          doLogout(true)
        } else {
          setSecondsLeft(Math.ceil(remaining / 1000))
        }
      }
    }

    const resetIdle = (broadcast: boolean) => {
      lastActivity = Date.now()
      if (warning) setWarn(false)
      if (broadcast) broadcastActivity()
    }

    const onActivity = () => {
      // Durante o aviso, atividade local é ignorada — só o botão cancela.
      if (warning) return
      resetIdle(true)
    }

    const onVisibility = () => {
      // Ao voltar o foco, recalcula imediatamente (cobre timers congelados em bg).
      if (document.visibilityState === 'visible') evaluate()
    }

    const staySignedInImpl = () => {
      resetIdle(true) // botão: reseta a janela e avisa as outras abas
    }
    stayRef.current = staySignedInImpl

    if (bc) {
      bc.onmessage = (e: MessageEvent) => {
        const type = (e.data as { type?: string } | null)?.type
        if (type === 'activity') {
          // Presença real em outra aba: reseta aqui (cancela aviso também).
          lastActivity = Date.now()
          if (warning) setWarn(false)
        } else if (type === 'logout') {
          doLogout(false)
        }
      }
    }

    tick = setInterval(evaluate, TICK_MS)
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }))
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stopped = true
      if (tick) clearInterval(tick)
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity))
      document.removeEventListener('visibilitychange', onVisibility)
      if (bc) {
        bc.onmessage = null
        bc.close()
      }
    }
  }, [enabled, isAuthenticated, pwaPersistent])

  return { warningActive, secondsLeft, staySignedIn }
}
